import React, { useCallback, useEffect, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import ClientSearchSelect from '../../components/ao/ClientSearchSelect';
import aoAPI from '../../api/aoAPI';

const fmt = (n) =>
  n ? new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2 }).format(n) : '—';

const EMPTY_FORM = {
  date: new Date().toISOString().substring(0, 10),
  staff: '',
  client: '',
  amount: '',
  remarks: '',
};

const AOPettyCashScreen = () => {
  const [staffList, setStaffList] = useState([]);
  const [clients, setClients] = useState([]);
  const [pettyCash, setPettyCash] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Load staff and clients
  useEffect(() => {
    const load = async () => {
      try {
        const [staffRes, clientRes] = await Promise.all([
          aoAPI.getStaff(),
          aoAPI.getClients(),
        ]);
        setStaffList(staffRes.data || []);
        setClients(clientRes.data || []);
      } catch (err) {
        console.error('Failed to load staff/clients', err);
      }
    };
    load();
  }, []);

  // Load petty cash list
  const loadPettyCash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await aoAPI.getPettyCash();
      setPettyCash(res.data || []);
    } catch (err) {
      console.error('Failed to load petty cash', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPettyCash();
  }, [loadPettyCash]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.staff || !form.amount) {
      alert('Please fill in required fields: Staff and Amount');
      return;
    }

    setSaving(true);
    setSaveMsg('');

    try {
      const payload = {
        staff: form.staff,
        client: form.client || null,
        date: form.date,
        amount: parseFloat(form.amount),
        remarks: form.remarks,
      };

      if (editId) {
        await aoAPI.updatePettyCash(editId, payload);
        setSaveMsg('Petty cash updated');
      } else {
        await aoAPI.createPettyCash(payload);
        setSaveMsg('Petty cash created');
      }

      setForm(EMPTY_FORM);
      setEditId(null);
      await loadPettyCash();

      setTimeout(() => setSaveMsg(''), 3000);
    } catch (err) {
      setSaveMsg('Error: ' + (err.message || 'Operation failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (record) => {
    setEditId(record._id);
    setForm({
      date: record.date.split('T')[0],
      staff: record.staff._id,
      client: record.client?._id || '',
      amount: record.amount,
      remarks: record.remarks,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this petty cash advance?')) return;
    try {
      await aoAPI.deletePettyCash(id);
      await loadPettyCash();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  // Group by staff
  const groupedByStaff = pettyCash.reduce((acc, pc) => {
    const staffId = pc.staff._id;
    if (!acc[staffId]) {
      acc[staffId] = {
        staffName: pc.staff.name,
        records: [],
      };
    }
    acc[staffId].records.push(pc);
    return acc;
  }, {});

  return (
    <Layout pageTitle="Petty Cash Entry">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        
        {/* Entry Form */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
          <h3 className="font-semibold text-gray-700 text-sm mb-4">
            {editId ? 'Edit Petty Cash' : 'New Petty Cash Advance'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* Date */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Staff */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Staff <span className="text-red-600">*</span>
                </label>
                <select
                  value={form.staff}
                  onChange={(e) => setForm({ ...form, staff: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">— Select staff —</option>
                  {staffList.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Amount (₱) <span className="text-red-600">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="0.00"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>

              {/* Client */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Client (optional)</label>
                <ClientSearchSelect
                  clients={clients}
                  value={form.client}
                  onChange={(val) => setForm({ ...form, client: val })}
                  placeholder="— None —"
                />
              </div>

              {/* Remarks */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-2">Remarks</label>
                <input
                  type="text"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                  placeholder="e.g. advance for BIR filing"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-between pt-2">
              <div>
                {saveMsg && (
                  <p className={saveMsg.includes('Error') ? 'text-red-600 text-sm' : 'text-green-600 text-sm'}>
                    {saveMsg}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {editId && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditId(null);
                      setForm(EMPTY_FORM);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : editId ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Petty Cash List by Staff */}
        {!loading && Object.keys(groupedByStaff).length > 0 && (
          <div className="space-y-4">
            {Object.entries(groupedByStaff).map(([staffId, group]) => (
              <div key={staffId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                  <h4 className="font-semibold text-gray-700 text-sm">{group.staffName}</h4>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="px-6 py-2 text-left font-medium text-gray-600">Date</th>
                      <th className="px-6 py-2 text-left font-medium text-gray-600">Client</th>
                      <th className="px-6 py-2 text-right font-medium text-gray-600">Amount</th>
                      <th className="px-6 py-2 text-left font-medium text-gray-600">Remarks</th>
                      <th className="px-6 py-2 text-right font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.records.map(record => (
                      <tr key={record._id} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="px-6 py-3 text-gray-700">
                          {new Date(record.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-3 text-gray-600 text-xs">
                          {record.client?.name ? `${record.client.code} — ${record.client.name}` : '—'}
                        </td>
                        <td className="px-6 py-3 text-right text-gray-700 font-medium">
                          ₱{fmt(record.amount)}
                        </td>
                        <td className="px-6 py-3 text-gray-600 text-xs">{record.remarks || '—'}</td>
                        <td className="px-6 py-3 flex gap-2 justify-end">
                          <button
                            onClick={() => handleEdit(record)}
                            className="text-teal-600 hover:text-teal-700"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(record._id)}
                            className="text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {!loading && pettyCash.length === 0 && (
          <div className="text-center py-12 text-gray-500">No petty cash advances yet.</div>
        )}

        {loading && (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        )}
      </div>
    </Layout>
  );
};

export default AOPettyCashScreen;
